import { Autocomplete as AutocompletePrimitive } from '@base-ui/react'

import { POPOVER_OPTION, controlShellClass, type ControlSize } from '@/components/ui/control'
import { cn } from '@/lib/cn'

/* The autocomplete: a text field that SUGGESTS rather than a picker that
 * constrains.
 *
 * ── Why this is not Combobox.tsx ────────────────────────────────────────────
 *
 * Base UI ships both, and they answer different questions. A Combobox owns a
 * VALUE chosen from a list — the country dropdown, where "Kazakhstan" is the
 * answer and anything else is a typo. An Autocomplete owns the TEXT, and the
 * list is help: whatever is typed stands, whether or not a row matched.
 *
 * The cost line's vendor is the second kind, and migration 0019 is why. It
 * records, as a decision rather than as an omission, that at quoting time the
 * agent "is frequently a name from an email thread and not yet a counterparty
 * record, and forcing a directory entry to save a draft line would make the
 * salesperson invent one". A Combobox here would be that forcing.
 *
 * ── What is shared, and what could not be ───────────────────────────────────
 *
 * The overlay, the list and the empty state are Combobox.tsx's own exports,
 * imported by the caller and rendered inside this Root. That is not a trick:
 * Base UI's `autocomplete` barrel re-exports Popup, Positioner, List, Portal
 * and Empty from `combobox/` unchanged — they are the same components reading
 * the same store — so a second styled copy of each would be two popups that
 * drift apart while claiming to be one control.
 *
 * Root, Input and Item are genuinely different components and are the three
 * this file adds. They are dressed from the same tokens as their Combobox
 * counterparts, because on screen the two must be indistinguishable: what
 * differs is whether an unmatched value survives, and a user should not have to
 * learn that from the border.
 */

export const Autocomplete = AutocompletePrimitive.Root

/** The field. Two shells, and a table is the reason there are two.
 *
 *  The default is the ordinary control box every other input on a form wears.
 *  `bare` drops it for a cell inside a data table, where a bordered box per
 *  column turns a costing into a spreadsheet — the row it sits in already draws
 *  the grid, and the field only needs to look editable on focus. */
export function AutocompleteInput({
  className,
  /* `inputSize`, not `size`: a native `<input>` HAS a `size` attribute — a
     number, the width in characters — so intersecting our own union onto these
     props collapses the field to `never`. Combobox.tsx names it the same way
     for the same collision. */
  inputSize = 'md',
  bare = false,
  ...props
}: AutocompletePrimitive.Input.Props & {
  inputSize?: ControlSize
  bare?: boolean
}) {
  return (
    <AutocompletePrimitive.Input
      className={cn(
        bare
          ? 'w-full min-w-0 rounded-[5px] border border-transparent bg-transparent px-1.5 py-0.5'
          : cn(controlShellClass(inputSize), 'px-3'),
        'text-text-heading font-sans text-[13px] font-normal',
        'placeholder:text-text-muted/60',
        'focus:border-brand focus:shadow-none focus:outline-none',
        'disabled:cursor-not-allowed',
        className,
      )}
      {...props}
    />
  )
}

/** One suggestion. `POPOVER_OPTION` and the highlight state, exactly as
 *  ComboboxItem carries them — see this file's header for why the two are
 *  dressed alike.
 *
 *  No tick. A Combobox item shows one because the control HAS a selected value
 *  to mark; here the value is whatever is in the field, so a row that drew a
 *  tick would be claiming a selection the control does not model. */
export function AutocompleteItem({
  className,
  ...props
}: AutocompletePrimitive.Item.Props) {
  return (
    <AutocompletePrimitive.Item
      className={cn(
        POPOVER_OPTION,
        'text-text-body font-normal',
        'data-highlighted:bg-surface-hover',
        className,
      )}
      {...props}
    />
  )
}
